
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Eye, ShoppingCart, DollarSign } from "lucide-react";

interface ProductAnalyticsCardProps {
  product: {
    id: string;
    name: string;
    price: number;
    image_url?: string;
    stock_quantity?: number;
  };
  analytics: {
    views: number;
    sales: number;
    revenue: number;
    conversion_rate: number;
    trend: 'up' | 'down' | 'stable';
  };
}

const ProductAnalyticsCard = ({ product, analytics }: ProductAnalyticsCardProps) => {
  const getTrendIcon = () => {
    switch (analytics.trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-600" />;
      default:
        return <div className="h-4 w-4" />;
    }
  };

  return (
    <Card className="admin-card border-admin-border">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          {product.image_url && (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-12 h-12 object-cover rounded"
            />
          )}
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-medium text-admin-foreground truncate">
              {product.name}
            </CardTitle>
            <p className="text-sm text-admin-muted-foreground">
              ${product.price.toFixed(2)}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {getTrendIcon()}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <Eye className="h-3 w-3 text-admin-muted-foreground" />
              <span className="text-xs text-admin-muted-foreground">Views</span>
            </div>
            <p className="text-lg font-semibold text-admin-foreground">
              {analytics.views.toLocaleString()}
            </p>
          </div>
          
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <ShoppingCart className="h-3 w-3 text-admin-muted-foreground" />
              <span className="text-xs text-admin-muted-foreground">Sales</span>
            </div>
            <p className="text-lg font-semibold text-admin-foreground">
              {analytics.sales.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-admin-muted-foreground" />
            <span className="text-xs text-admin-muted-foreground">Revenue</span>
          </div>
          <p className="text-lg font-semibold text-admin-foreground">
            ${analytics.revenue.toFixed(2)}
          </p>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-admin-border">
          <span className="text-xs text-admin-muted-foreground">Conversion</span>
          <Badge variant={analytics.conversion_rate > 2 ? "default" : "secondary"}>
            {analytics.conversion_rate.toFixed(1)}%
          </Badge>
        </div>

        {product.stock_quantity !== undefined && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-admin-muted-foreground">Stock</span>
            <Badge 
              variant={product.stock_quantity > 10 ? "default" : product.stock_quantity > 0 ? "secondary" : "destructive"}
            >
              {product.stock_quantity}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductAnalyticsCard;
